import { CheckCircle, EventBusy, RemoveCircle } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useConvertTime } from '@/hooks'
import { preview } from '@/components/form/preview'

import { useTexts } from '../useTexts'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
}

export function Status(props: Props) {
  const { extOrder } = props
  const texts = useTexts()

  const trackingData = extOrder.orderStep.trackingData
  const status = trackingData?.status
  const statusSec = trackingData?.statusSec || 0
  const statusReason = trackingData?.statusReason

  const getStatusText = (
    status: uui.domain.client.rm.OrderStepTrackingData['status'] | null | undefined,
  ): string => {
    if (!status) {
      return texts.statusMissing.label
    }

    switch (status) {
      case 'missing':
        return texts.statusMissing.label
      case 'reschedule':
        return texts.statusReschedule.label
      case 'done':
      default:
        return texts.statusDone.label
    }
  }

  const getStatusDisplay = () => {
    if (status === 'done') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle sx={{ color: 'success.main', fontSize: '1.25rem' }} />
          <Typography>{getStatusText(status)}</Typography>
        </Box>
      )
    }

    if (status === 'reschedule') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventBusy sx={{ color: 'warning.main', fontSize: '1.25rem' }} />
            <Typography>{getStatusText(status)}</Typography>
          </Box>
          {statusReason && (
            <Box sx={{ pl: 4 }}>
              <Typography variant="caption" color="text.secondary">
                {texts.reportedStatusReason.label}
              </Typography>
              <Typography variant="body2">{statusReason}</Typography>
            </Box>
          )}
        </Box>
      )
    }

    // Handle null, undefined, or 'missing' status
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <RemoveCircle sx={{ color: 'action.disabled', fontSize: '1.25rem' }} />
        <Typography>{getStatusText(status)}</Typography>
      </Box>
    )
  }

  const formattedTime = useConvertTime(statusSec, false)

  const getLabel = () => {
    if (!status || status === 'missing') {
      return texts.reportedStatus.label
    }
    if (statusSec > 0) {
      return texts.reportedStatusWithDate(formattedTime)
    }
    return texts.status.label
  }

  return (
    <preview.Field label={getLabel()} testId="execution-reported-status">
      {getStatusDisplay()}
    </preview.Field>
  )
}
